import React, { useState, useEffect, useCallback } from 'react';
import { settingsAPI, pushAPI, teamAPI, currenciesAPI, mpesaAPI, paymentGatewaysAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useDateFormat } from '@/context/DateFormatContext';
import { DATE_FORMATS, TIME_FORMATS } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, Save, Settings as SettingsIcon, User, Clock, Bell, Globe, BellRing, Send, Users, UserPlus, Trash2, Coins, MapPin, Smartphone, CheckCircle, XCircle, CalendarDays, Lock, Eye, EyeOff, Star, MessageSquare } from 'lucide-react';

const TIMEZONES = ['UTC', 'US/Eastern', 'US/Central', 'US/Mountain', 'US/Pacific', 'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Asia/Bangkok', 'Australia/Sydney', 'Africa/Nairobi', 'Africa/Lagos', 'Africa/Johannesburg', 'Africa/Cairo'];

// Countries list
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'KE', name: 'Kenya' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'TZ', name: 'Tanzania' },
  { code: 'UG', name: 'Uganda' },
  { code: 'GH', name: 'Ghana' },
  { code: 'EG', name: 'Egypt' },
  { code: 'MA', name: 'Morocco' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'AU', name: 'Australia' },
  { code: 'CA', name: 'Canada' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'IN', name: 'India' },
  { code: 'CN', name: 'China' },
  { code: 'JP', name: 'Japan' },
  { code: 'SG', name: 'Singapore' },
  { code: 'TH', name: 'Thailand' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'NZ', name: 'New Zealand' },
].sort((a, b) => a.name.localeCompare(b.name));

const SettingsPage = () => {
  const { user } = useAuth();
  const { setDateFormat: updateGlobalDateFormat, setTimeFormat: updateGlobalTimeFormat } = useDateFormat();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [inviting, setInviting] = useState(false);
  const [currencies, setCurrencies] = useState({});
  
  // M-Pesa state
  const [mpesaConfig, setMpesaConfig] = useState({
    enabled: false,
    consumer_key: '',
    consumer_secret: '',
    shortcode: '',
    passkey: '',
    environment: 'sandbox',
    callback_url: ''
  });
  const [mpesaSaving, setMpesaSaving] = useState(false);
  const [mpesaTesting, setMpesaTesting] = useState(false);
  const [mpesaTestResult, setMpesaTestResult] = useState(null);

  // Pesapal state
  const [pesapalConfig, setPesapalConfig] = useState({
    enabled: false,
    consumer_key: '',
    consumer_secret: '',
    environment: 'sandbox',
    ipn_id: ''
  });
  const [pesapalSaving, setPesapalSaving] = useState(false);
  const [pesapalTestResult, setPesapalTestResult] = useState(null);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const checkPushStatus = useCallback(async () => {
    try {
      const resp = await pushAPI.status();
      setPushEnabled(resp.data.subscribed);
    } catch {}
  }, []);

  const loadTeam = useCallback(async () => {
    try {
      const resp = await teamAPI.list();
      setTeamMembers(resp.data);
    } catch {}
  }, []);

  const loadCurrencies = useCallback(async () => {
    try {
      const resp = await currenciesAPI.list();
      setCurrencies(resp.data);
    } catch {}
  }, []);

  const loadMpesaConfig = useCallback(async () => {
    try {
      const resp = await mpesaAPI.getConfig();
      setMpesaConfig(resp.data);
    } catch {}
  }, []);

  const loadPesapalConfig = useCallback(async () => {
    try {
      const resp = await paymentGatewaysAPI.getHostConfig();
      if (resp.data?.pesapal) {
        setPesapalConfig(prev => ({ ...prev, ...resp.data.pesapal }));
      }
    } catch {}
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await settingsAPI.get();
        setSettings(resp.data);
      } catch (err) {
        console.error('Settings error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
    checkPushStatus();
    loadTeam();
    loadCurrencies();
    loadMpesaConfig();
    loadPesapalConfig();
  }, [checkPushStatus, loadTeam, loadCurrencies, loadMpesaConfig, loadPesapalConfig]);

  const handleChange = (name, value) => {
    setSettings(prev => ({ ...prev, [name]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    // Validation
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordChanging(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
        },
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        })
      });

      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to change password');
      }

      setPasswordSuccess(true);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    } finally {
      setPasswordChanging(false);
    }
  };

  // M-Pesa handlers
  const handleMpesaChange = (name, value) => {
    setMpesaConfig(prev => ({ ...prev, [name]: value }));
    setMpesaTestResult(null);
  };

  const handleMpesaSave = async () => {
    setMpesaSaving(true);
    try {
      await mpesaAPI.updateConfig(mpesaConfig);
      setMpesaTestResult({ success: true, message: 'M-Pesa configuration saved' });
      setTimeout(() => setMpesaTestResult(null), 3000);
    } catch (err) {
      console.error('M-Pesa save error:', err);
      setMpesaTestResult({ success: false, message: 'Failed to save configuration' });
    } finally {
      setMpesaSaving(false);
    }
  };

  const handleMpesaTest = async () => {
    setMpesaTesting(true);
    setMpesaTestResult(null);
    try {
      // Save first, then test
      await mpesaAPI.updateConfig(mpesaConfig);
      const resp = await mpesaAPI.testConfig();
      setMpesaTestResult(resp.data);
    } catch (err) {
      console.error('M-Pesa test error:', err);
      setMpesaTestResult({ success: false, message: 'Test failed: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setMpesaTesting(false);
    }
  };

  // Pesapal handlers
  const handlePesapalChange = (name, value) => {
    setPesapalConfig(prev => ({ ...prev, [name]: value }));
    setPesapalTestResult(null);
  };

  const handlePesapalSave = async () => {
    setPesapalSaving(true);
    try {
      await paymentGatewaysAPI.updateHostConfig({
        use_host_settings: false,
        stripe: { enabled: false },
        mpesa: { enabled: mpesaConfig.enabled, ...mpesaConfig },
        pesapal: { enabled: pesapalConfig.enabled, ...pesapalConfig }
      });
      setPesapalTestResult({ success: true, message: 'Pesapal configuration saved' });
      setTimeout(() => setPesapalTestResult(null), 3000);
    } catch (err) {
      console.error('Pesapal save error:', err);
      setPesapalTestResult({ success: false, message: 'Failed to save configuration' });
    } finally {
      setPesapalSaving(false);
    }
  };

  const handlePesapalTest = async () => {
    setPesapalTestResult({ loading: true, message: 'Testing connection...' });
    try {
      await handlePesapalSave();
      const resp = await paymentGatewaysAPI.testPesapal();
      setPesapalTestResult(resp.data);
    } catch (err) {
      setPesapalTestResult({ success: false, message: 'Test failed: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleTogglePush = async (enable) => {
    setPushLoading(true);
    try {
      if (enable) {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          setPushLoading(false);
          return;
        }
        const reg = await navigator.serviceWorker.ready;
        const keyResp = await pushAPI.getVapidKey();
        const vapidKey = keyResp.data.public_key;

        // Convert VAPID key to Uint8Array
        const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
        const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: outputArray,
        });
        await pushAPI.subscribe({ subscription_info: subscription.toJSON() });
        setPushEnabled(true);
      } else {
        const reg = await navigator.serviceWorker.ready;
        const subscription = await reg.pushManager.getSubscription();
        if (subscription) {
          await pushAPI.unsubscribe({ subscription_info: subscription.toJSON() });
          await subscription.unsubscribe();
        }
        setPushEnabled(false);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setTestSending(true);
    try {
      await pushAPI.test({ title: 'iHostPMS Test', body: 'Push notifications are working!' });
    } catch (err) {
      console.error('Test push error:', err);
    } finally {
      setTestSending(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await teamAPI.invite(newMember);
      setInviteOpen(false);
      setNewMember({ name: '', email: '', password: '', role: 'staff' });
      loadTeam();
    } catch (err) {
      console.error('Invite error:', err);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await teamAPI.remove(userId);
      loadTeam();
    } catch (err) {
      console.error('Remove member error:', err);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;

  return (
    <div data-testid="settings-page" className="animate-fade-in max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-airbnb-gray">Settings</h1>
        <p className="text-airbnb-medium-gray mt-1">Manage your account and preferences</p>
      </div>

      <div className="space-y-5">
        {/* Profile Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <User className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Profile</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Name</Label>
              <Input value={user?.name || ''} disabled className="mt-1 bg-gray-50" />
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Email</Label>
              <Input value={user?.email || ''} disabled className="mt-1 bg-gray-50" />
            </div>
          </div>
        </div>

        {/* Change Password Section */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Change Password</h2>
          </div>
          <form onSubmit={handlePasswordChange} className="max-w-md space-y-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm(f => ({ ...f, current_password: e.target.value }))}
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">New Password</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm(f => ({ ...f, new_password: e.target.value }))}
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(f => ({ ...f, confirm_password: e.target.value }))}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>

            {passwordError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center gap-2">
                <XCircle className="w-4 h-4 flex-shrink-0" />
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-600 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                Password changed successfully!
              </div>
            )}

            <Button
              type="submit"
              disabled={passwordChanging}
              className="bg-airbnb-pink hover:bg-airbnb-pink/90"
              data-testid="change-password-btn"
            >
              {passwordChanging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </form>
        </div>

        {/* Company Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <SettingsIcon className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Company</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-sm font-medium text-airbnb-gray">Company Name</Label>
              <Input data-testid="settings-company-name" value={settings?.company_name || ''} onChange={(e) => handleChange('company_name', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Timezone</Label>
              <select data-testid="settings-timezone" value={settings?.timezone || 'UTC'} onChange={(e) => handleChange('timezone', e.target.value)} className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white">
                {TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Notification Email</Label>
              <Input data-testid="settings-notification-email" type="email" value={settings?.notification_email || ''} onChange={(e) => handleChange('notification_email', e.target.value)} className="mt-1" placeholder="Where to receive booking alerts" />
            </div>
          </div>
        </div>

        {/* Currency & Location Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-2">
            <Coins className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Currency & Location</h2>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Set your home country and base currency for dashboard reporting. You can set different currencies per property for OTA channels.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" /> Country of Origin
              </Label>
              <select 
                data-testid="settings-country" 
                value={settings?.country_of_origin || ''} 
                onChange={(e) => handleChange('country_of_origin', e.target.value)} 
                className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              >
                <option value="">Select country...</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1">Where your business is registered</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray flex items-center gap-1">
                <Coins className="w-3.5 h-3.5" /> Base Currency (Dashboard)
              </Label>
              <select 
                data-testid="settings-base-currency" 
                value={settings?.base_currency || 'USD'} 
                onChange={(e) => handleChange('base_currency', e.target.value)} 
                className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              >
                {Object.entries(currencies).map(([code, config]) => (
                  <option key={code} value={code}>{config.symbol} {code} - {config.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Currency shown on dashboard and reports</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Default Property Currency</Label>
              <select 
                data-testid="settings-currency" 
                value={settings?.default_currency || 'USD'} 
                onChange={(e) => handleChange('default_currency', e.target.value)} 
                className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              >
                {Object.entries(currencies).map(([code, config]) => (
                  <option key={code} value={code}>{config.symbol} {code} - {config.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Default for new properties</p>
            </div>
          </div>
        </div>

        {/* Date & Time Format Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-2">
            <CalendarDays className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Date & Time Format</h2>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Choose how dates and times are displayed throughout the application. This setting does not affect how dates are stored or sent to integrations.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Date Format</Label>
              <select 
                data-testid="settings-date-format" 
                value={settings?.date_format || 'DD/MM/YYYY'} 
                onChange={(e) => {
                  handleChange('date_format', e.target.value);
                  updateGlobalDateFormat(e.target.value);
                }} 
                className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              >
                {Object.entries(DATE_FORMATS).map(([format, { label }]) => (
                  <option key={format} value={format}>{label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Affects date display only</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Time Format</Label>
              <select 
                data-testid="settings-time-format" 
                value={settings?.time_format || '24h'} 
                onChange={(e) => {
                  handleChange('time_format', e.target.value);
                  updateGlobalTimeFormat(e.target.value);
                }} 
                className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
              >
                {Object.entries(TIME_FORMATS).map(([format, { label }]) => (
                  <option key={format} value={format}>{label}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Affects check-in/check-out time display</p>
            </div>
          </div>
        </div>

        {/* Booking Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Clock className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Booking Defaults</h2>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Check-in Time</Label>
              <Input data-testid="settings-checkin-time" type="time" value={settings?.default_check_in_time || '15:00'} onChange={(e) => handleChange('default_check_in_time', e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Check-out Time</Label>
              <Input data-testid="settings-checkout-time" type="time" value={settings?.default_check_out_time || '11:00'} onChange={(e) => handleChange('default_check_out_time', e.target.value)} className="mt-1" />
            </div>
          </div>
        </div>

        {/* M-Pesa Configuration */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-bold text-airbnb-gray">M-Pesa Payments</h2>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="mpesa-enabled"
                checked={mpesaConfig.enabled}
                onCheckedChange={(checked) => handleMpesaChange('enabled', checked)}
              />
              <span className="text-sm text-airbnb-medium-gray">{mpesaConfig.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Accept M-Pesa payments for all property bookings. Guests receive STK push on their phones.</p>
          
          {mpesaConfig.enabled && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Consumer Key</Label>
                  <Input 
                    data-testid="mpesa-consumer-key"
                    value={mpesaConfig.consumer_key || ''} 
                    onChange={(e) => handleMpesaChange('consumer_key', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="From Daraja portal"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Consumer Secret</Label>
                  <Input 
                    data-testid="mpesa-consumer-secret"
                    type="password"
                    value={mpesaConfig.consumer_secret || ''} 
                    onChange={(e) => handleMpesaChange('consumer_secret', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="From Daraja portal"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Shortcode (Paybill/Till)</Label>
                  <Input 
                    data-testid="mpesa-shortcode"
                    value={mpesaConfig.shortcode || ''} 
                    onChange={(e) => handleMpesaChange('shortcode', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="e.g., 174379"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Your M-Pesa Paybill or Till number</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Passkey</Label>
                  <Input 
                    data-testid="mpesa-passkey"
                    type="password"
                    value={mpesaConfig.passkey || ''} 
                    onChange={(e) => handleMpesaChange('passkey', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="From Daraja portal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Environment</Label>
                  <select 
                    data-testid="mpesa-environment"
                    value={mpesaConfig.environment || 'sandbox'} 
                    onChange={(e) => handleMpesaChange('environment', e.target.value)}
                    className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Callback URL (Optional)</Label>
                  <Input 
                    data-testid="mpesa-callback-url"
                    value={mpesaConfig.callback_url || ''} 
                    onChange={(e) => handleMpesaChange('callback_url', e.target.value)} 
                    className="mt-1 text-sm" 
                    placeholder="Leave empty for default"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">Custom URL to receive payment confirmations</p>
                </div>
              </div>

              {mpesaTestResult && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${mpesaTestResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {mpesaTestResult.success ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  <span className="text-sm">{mpesaTestResult.message}</span>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button 
                  onClick={handleMpesaSave} 
                  disabled={mpesaSaving}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  data-testid="mpesa-save-btn"
                >
                  {mpesaSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Save M-Pesa Config
                </Button>
                <Button 
                  onClick={handleMpesaTest} 
                  disabled={mpesaTesting || !mpesaConfig.consumer_key}
                  variant="outline"
                  className="rounded-xl"
                  data-testid="mpesa-test-btn"
                >
                  {mpesaTesting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Test Connection
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Get your M-Pesa API credentials from the{' '}
                  <a href="https://developer.safaricom.co.ke/" target="_blank" rel="noopener noreferrer" className="underline">Safaricom Daraja Portal</a>.
                  Use sandbox for testing before switching to production.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pesapal Payments */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-bold text-airbnb-gray">Pesapal Payments</h2>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="pesapal-enabled"
                checked={pesapalConfig.enabled}
                onCheckedChange={(checked) => handlePesapalChange('enabled', checked)}
              />
              <span className="text-sm text-airbnb-medium-gray">{pesapalConfig.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Accept payments via Pesapal (cards, M-Pesa, bank transfers) across Africa.</p>
          
          {pesapalConfig.enabled && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Consumer Key</Label>
                  <Input 
                    data-testid="pesapal-consumer-key"
                    value={pesapalConfig.consumer_key || ''} 
                    onChange={(e) => handlePesapalChange('consumer_key', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="From Pesapal Dashboard"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Consumer Secret</Label>
                  <Input 
                    data-testid="pesapal-consumer-secret"
                    type="password"
                    value={pesapalConfig.consumer_secret || ''} 
                    onChange={(e) => handlePesapalChange('consumer_secret', e.target.value)} 
                    className="mt-1 font-mono text-sm" 
                    placeholder="From Pesapal Dashboard"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Environment</Label>
                  <select 
                    data-testid="pesapal-environment"
                    value={pesapalConfig.environment || 'sandbox'} 
                    onChange={(e) => handlePesapalChange('environment', e.target.value)}
                    className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                  >
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">IPN ID</Label>
                  <Input 
                    value={pesapalConfig.ipn_id || ''} 
                    disabled
                    className="mt-1 font-mono text-sm bg-gray-50" 
                    placeholder="Auto-generated on registration"
                  />
                </div>
              </div>

              {pesapalTestResult && (
                <div className={`flex items-center gap-2 p-3 rounded-lg ${pesapalTestResult.success ? 'bg-green-50 text-green-700' : pesapalTestResult.loading ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'}`}>
                  {pesapalTestResult.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : pesapalTestResult.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{pesapalTestResult.message}</span>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button 
                  onClick={handlePesapalSave} 
                  disabled={pesapalSaving}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                  data-testid="pesapal-save-btn"
                >
                  {pesapalSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                  Save Pesapal Config
                </Button>
                <Button 
                  onClick={handlePesapalTest} 
                  disabled={!pesapalConfig.consumer_key}
                  variant="outline"
                  className="rounded-xl"
                  data-testid="pesapal-test-btn"
                >
                  Test Connection
                </Button>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-orange-800">
                  <strong>Note:</strong> Get your Pesapal API credentials from the{' '}
                  <a href="https://www.pesapal.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline">Pesapal Dashboard</a>.
                  Use sandbox for testing before switching to production.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Bell className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Notifications</h2>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Notification Email</Label>
              <Input data-testid="settings-notification-email" value={settings?.notification_email || ''} onChange={(e) => handleChange('notification_email', e.target.value)} className="mt-1" type="email" />
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Auto-confirm Bookings</Label>
                <p className="text-xs text-airbnb-medium-gray mt-0.5">Automatically confirm new bookings from channels</p>
              </div>
              <Switch
                data-testid="settings-auto-confirm"
                checked={settings?.auto_confirm_bookings || false}
                onCheckedChange={(checked) => handleChange('auto_confirm_bookings', checked)}
              />
            </div>
          </div>
        </div>

        {/* Team Management */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-airbnb-pink" />
              <h2 className="text-lg font-bold text-airbnb-gray">Team Management</h2>
            </div>
            {(user?.role === 'owner' || user?.role === 'manager') && (
              <Button onClick={() => setInviteOpen(true)} size="sm" className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl text-xs" data-testid="invite-member-btn">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Invite
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {teamMembers.map(m => (
              <div key={m.user_id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-xl" data-testid={`team-${m.user_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-airbnb-light-pink flex items-center justify-center text-xs font-bold text-airbnb-pink">{m.name?.charAt(0)?.toUpperCase()}</div>
                  <div>
                    <p className="text-sm font-medium text-airbnb-gray">{m.name}</p>
                    <p className="text-xs text-airbnb-medium-gray">{m.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold bg-gray-100 text-airbnb-medium-gray px-2 py-0.5 rounded-full capitalize">{m.role}</span>
                  {m.role !== 'owner' && (user?.role === 'owner' || user?.role === 'manager') && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600" onClick={() => handleRemoveMember(m.user_id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <BellRing className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Push Notifications</h2>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-4">Receive instant notifications for new bookings, calendar syncs, and important alerts.</p>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Enable Push Notifications</Label>
                <p className="text-xs text-airbnb-medium-gray mt-0.5">Get notified about new bookings and iCal sync events</p>
              </div>
              <Switch
                data-testid="push-toggle"
                checked={pushEnabled}
                disabled={pushLoading}
                onCheckedChange={handleTogglePush}
              />
            </div>
            {pushEnabled && (
              <Button
                data-testid="test-push-btn"
                variant="outline"
                size="sm"
                onClick={handleTestPush}
                disabled={testSending}
                className="rounded-xl"
              >
                {testSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Test Notification
              </Button>
            )}
          </div>
        </div>

        {/* Cloudinary (per-tenant) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Globe className="w-5 h-5 text-airbnb-pink" />
            <h2 className="text-lg font-bold text-airbnb-gray">Cloudinary (Photo Upload)</h2>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-4">Optional: Connect your own Cloudinary account for cloud-hosted property photos.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Cloud Name</Label>
              <Input data-testid="settings-cloudinary-name" value={settings?.cloudinary?.cloud_name || ''} onChange={(e) => handleChange('cloudinary', { ...settings?.cloudinary, cloud_name: e.target.value })} className="mt-1" placeholder="my-cloud" />
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">API Key</Label>
              <Input value={settings?.cloudinary?.api_key || ''} onChange={(e) => handleChange('cloudinary', { ...settings?.cloudinary, api_key: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">API Secret</Label>
              <Input type="password" value={settings?.cloudinary?.api_secret || ''} onChange={(e) => handleChange('cloudinary', { ...settings?.cloudinary, api_secret: e.target.value })} className="mt-1" />
            </div>
          </div>
        </div>

        {/* Guest Reviews Settings */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              <h2 className="text-lg font-bold text-airbnb-gray">Guest Reviews</h2>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="reviews-enabled"
                checked={settings?.reviews?.enabled || false}
                onCheckedChange={(checked) => handleChange('reviews', { ...settings?.reviews, enabled: checked })}
              />
              <span className="text-sm text-airbnb-medium-gray">{settings?.reviews?.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Collect and display guest reviews on your property listings and the marketplace.</p>
          
          {settings?.reviews?.enabled && (
            <div className="space-y-4">
              {/* Review Request Mode */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Auto-request Reviews</Label>
                  <p className="text-xs text-airbnb-medium-gray mt-0.5">Automatically send review request emails after checkout</p>
                </div>
                <Switch
                  data-testid="reviews-auto-request"
                  checked={settings?.reviews?.request_mode === 'auto'}
                  onCheckedChange={(checked) => handleChange('reviews', { ...settings?.reviews, request_mode: checked ? 'auto' : 'manual' })}
                />
              </div>

              {/* Days After Checkout */}
              {settings?.reviews?.request_mode === 'auto' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-airbnb-gray">Days After Checkout</Label>
                    <select
                      data-testid="reviews-days-after"
                      value={settings?.reviews?.days_after_checkout || 1}
                      onChange={(e) => handleChange('reviews', { ...settings?.reviews, days_after_checkout: parseInt(e.target.value) })}
                      className="mt-1 w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                    >
                      <option value={1}>1 day</option>
                      <option value={2}>2 days</option>
                      <option value={3}>3 days</option>
                      <option value={5}>5 days</option>
                      <option value={7}>7 days</option>
                    </select>
                    <p className="text-xs text-gray-400 mt-1">When to send the review request email</p>
                  </div>
                </div>
              )}

              {/* Auto-publish */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Auto-publish Reviews</Label>
                  <p className="text-xs text-airbnb-medium-gray mt-0.5">Automatically publish all reviews (otherwise require approval)</p>
                </div>
                <Switch
                  data-testid="reviews-auto-publish"
                  checked={settings?.reviews?.auto_publish || false}
                  onCheckedChange={(checked) => handleChange('reviews', { ...settings?.reviews, auto_publish: checked })}
                />
              </div>

              {/* Allow OTA Import */}
              <div className="flex items-center justify-between py-3">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Allow OTA Review Import</Label>
                  <p className="text-xs text-airbnb-medium-gray mt-0.5">Import reviews from Airbnb, Booking.com etc.</p>
                </div>
                <Switch
                  data-testid="reviews-allow-ota"
                  checked={settings?.reviews?.allow_ota_import !== false}
                  onCheckedChange={(checked) => handleChange('reviews', { ...settings?.reviews, allow_ota_import: checked })}
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                <p className="text-xs text-yellow-800 flex items-start gap-2">
                  <MessageSquare className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Tip:</strong> Manage all reviews from the <a href="/reviews" className="underline font-medium">Reviews page</a>. 
                    You can request reviews manually from completed bookings and respond to guest feedback.
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Email Templates */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Send className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-bold text-airbnb-gray">Email Templates</h2>
          </div>
          <p className="text-sm text-airbnb-medium-gray mb-5">Customize the emails sent to your guests. Use variables like {'{guest_name}'}, {'{property_name}'}, {'{review_link}'}, {'{check_in}'}, {'{check_out}'}.</p>
          
          <div className="space-y-5">
            {/* Review Request Email Template */}
            <div className="border border-gray-100 rounded-xl p-4">
              <h3 className="font-semibold text-airbnb-gray mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Review Request Email
              </h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Subject Line</Label>
                  <Input 
                    data-testid="email-review-subject"
                    value={settings?.email_templates?.review_request?.subject || ''}
                    onChange={(e) => handleChange('email_templates', { 
                      ...settings?.email_templates, 
                      review_request: { 
                        ...settings?.email_templates?.review_request, 
                        subject: e.target.value 
                      }
                    })}
                    placeholder="How was your stay at {property_name}?"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray">Email Body (HTML supported)</Label>
                  <textarea
                    data-testid="email-review-body"
                    value={settings?.email_templates?.review_request?.body || ''}
                    onChange={(e) => handleChange('email_templates', { 
                      ...settings?.email_templates, 
                      review_request: { 
                        ...settings?.email_templates?.review_request, 
                        body: e.target.value 
                      }
                    })}
                    placeholder={`Hi {guest_name},\n\nThank you for staying at {property_name}!\n\nWe'd love to hear about your experience.\n\n{review_link}\n\nBest regards`}
                    rows={6}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-y"
                  />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to use the default template. Variables: {'{guest_name}'}, {'{property_name}'}, {'{review_link}'}, {'{check_in}'}, {'{check_out}'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <Button data-testid="settings-save-btn" onClick={handleSave} disabled={saving} className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl px-8">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
          {saved && <span className="text-sm text-emerald-600 font-medium">Settings saved!</span>}
        </div>
      </div>

      {/* Invite Team Member Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-airbnb-pink" /> Invite Team Member</DialogTitle>
            <DialogDescription className="sr-only">Invite a new team member</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteMember} className="space-y-4">
            <div><Label className="text-sm">Name</Label><Input value={newMember.name} onChange={e => setNewMember(m => ({...m, name: e.target.value}))} required className="mt-1" /></div>
            <div><Label className="text-sm">Email</Label><Input type="email" value={newMember.email} onChange={e => setNewMember(m => ({...m, email: e.target.value}))} required className="mt-1" /></div>
            <div><Label className="text-sm">Password</Label><Input type="password" value={newMember.password} onChange={e => setNewMember(m => ({...m, password: e.target.value}))} required className="mt-1" /></div>
            <div><Label className="text-sm">Role</Label>
              <select value={newMember.role} onChange={e => setNewMember(m => ({...m, role: e.target.value}))} className="w-full h-9 mt-1 px-3 border border-gray-200 rounded-lg text-sm">
                <option value="manager">Manager</option><option value="staff">Staff</option>
              </select>
            </div>
            <Button type="submit" disabled={inviting} className="w-full bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl" data-testid="submit-invite-btn">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />} Invite
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
